import React, { memo } from 'react';
import '@xyflow/react/dist/style.css';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Shield, EyeOff, FileText, CreditCard, Layers, Wallet, Activity, Leaf } from 'lucide-react';
const GridCell = ({
  icon: Icon,
  label,
  value,
  statusColor
}: {
  icon: any;
  label: string;
  value: string;
  statusColor?: string;
}) => {
  const dotColor = statusColor === 'text-amber-500' ? 'bg-amber-500' : 'bg-emerald-500';
  return <div className="flex items-center gap-2.5 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200" data-id="element-96" data-mp="rnww3bu4gl48">
      <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-97" data-mp="vp8fgzmeka3z" />
      <span className="text-gray-600 text-[12px] font-medium" data-id="element-98" data-mp="hv7qgxl961gt">{label}</span>
      <div className="ml-auto flex items-center gap-1.5" data-id="element-99" data-mp="muajfjnykh8f">
        <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`} data-id="element-100" data-mp="msr2dquha25l" />
        <span className="text-[11px] font-medium text-gray-600" data-id="element-101" data-mp="1ajwk6bjzb4ij">{value}</span>
      </div>
    </div>;
};
const EngineNode = ({
  selected,
  data
}: NodeProps<any>) => {
  const focusedService = data?.focusedService;
  const providerAggregate = data?.providerAggregate;
  const hasFocus = !!focusedService;
  const hasProviderFocus = !!providerAggregate;
  const complianceValue = hasFocus ? focusedService.compliance : hasProviderFocus ? providerAggregate.compliance : 81;
  const complianceOk = complianceValue >= 80;
  const totalRequests = hasFocus ? focusedService.requestsPerDay?.toLocaleString() : hasProviderFocus ? providerAggregate.requestsPerDay.toLocaleString() : '17,080';
  const totalCost = hasFocus ? `€${focusedService.cost}` : hasProviderFocus ? `€${providerAggregate.cost}` : '€214';
  const serviceCount = hasFocus ? '1' : hasProviderFocus ? String(providerAggregate.count) : '8';
  // ECO Impact: kg CO₂ emitted by AI consumption per month
  const ecoImpact = hasFocus ? focusedService.ecoScore ?? 12 : hasProviderFocus ? providerAggregate.ecoScore ?? 38 : 42;
  const ecoLabel = `${ecoImpact} kg`;
  const ecoIsLow = ecoImpact < 20;
  return <div className={`
        relative p-6 rounded-lg w-[900px] transition-all duration-300
        bg-white border shadow-lg
        ${selected ? 'border-[#00B4D8] ring-1 ring-[#00B4D8]' : 'border-gray-200'}
      `} data-id="element-102" data-mp="3pqzhyerxtjqn">
      <Handle type="target" position={Position.Top} className="!bg-[#00B4D8] !w-5 !h-2 !rounded-sm !border-0" data-id="element-103" data-mp="3j9leu97potk" />
      <Handle type="source" position={Position.Bottom} className="!bg-[#00B4D8] !w-5 !h-2 !rounded-sm !border-0" data-id="element-104" data-mp="7lw86gr10jjw" />

      {/* Title */}
      <div className="flex items-center gap-3 mb-5" data-id="element-105" data-mp="ci3g2f2zg54h">
        <h2 className="text-[#2a3a54] font-semibold text-[18px] tracking-tight" data-id="element-106" data-mp="hi913ceqytdl">
          AI GOVERNANCE ENGINE
        </h2>
        <span className="text-gray-500 text-[11px] uppercase tracking-wider hidden sm:inline" data-id="element-107" data-mp="maivb4mwaflo">
          {hasFocus ? `Filtered: ${focusedService.name}` : hasProviderFocus ? `Provider: ${providerAggregate.count} services` : 'Governance & Orchestration'}
        </span>
      </div>

      {/* Key Metrics — large, prominent */}
      <div className="grid grid-cols-5 gap-3 mb-5" data-id="element-108" data-mp="107fld3v6vt1i">
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-109" data-mp="2ksvyap09d959">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-110" data-mp="2fs84xwok4g7v">
            <Layers className="w-3.5 h-3.5 text-gray-400" data-id="element-111" data-mp="2fuu9rok2chme" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-112" data-mp="2bs657t2dtwv8">
              Services
            </span>
          </div>
          <span className="text-[22px] font-bold text-[#2a3a54] leading-none" data-id="element-113" data-mp="26u4hguu0h9df">
            {serviceCount}
          </span>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-114" data-mp="2zg77wlcvqj2q">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-115" data-mp="2uq0kgou3t4m2">
            <Wallet className="w-3.5 h-3.5 text-gray-400" data-id="element-116" data-mp="2uofpwz7shlo7" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-117" data-mp="2prfjuoaxwix3">
              Costo Mensile
            </span>
          </div>
          <span className="text-[22px] font-bold text-[#2a3a54] leading-none" data-id="element-118" data-mp="3n7mbehge76fk">
            {totalCost}
          </span>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-119" data-mp="32026vwb95aa4">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-120" data-mp="3226jr3r4pjmj">
            <Activity className="w-3.5 h-3.5 text-gray-400" data-id="element-121" data-mp="370803gx674ub" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-122" data-mp="2szdut75f5jzv">
              Requests/Day
            </span>
          </div>
          <span className="text-[22px] font-bold text-[#2a3a54] leading-none" data-id="element-123" data-mp="2xz9voxitkvb7">
            {totalRequests}
          </span>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-124" data-mp="3lqfaragxrjkg">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-125" data-mp="3pp4udkskhxcj">
            <Leaf className="w-3.5 h-3.5 text-gray-400" data-id="element-126" data-mp="3bz1h3jbo1dz8" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-127" data-mp="3bwfapn0ih4il">
              ECO Impact
            </span>
          </div>
          <span className="text-[22px] font-bold text-[#2a3a54] leading-none" data-id="element-128" data-mp="8h9i3kz43trj">
            {ecoLabel}
          </span>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-129" data-mp="d9su4di5igu8">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-130" data-mp="mm035osk7x95">
            <Shield className="w-3.5 h-3.5 text-gray-400" data-id="element-131" data-mp="ilg4uhw92nlf" />
            <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-132" data-mp="ikyaxrx28lgo">
              Compliance
            </span>
          </div>
          <span className={`text-[22px] font-bold leading-none ${complianceOk ? 'text-emerald-500' : 'text-amber-500'}`} data-id="element-133" data-mp="rdryecmnur5m">
            {complianceValue}%
          </span>
        </div>
      </div>

      {/* Modules — compact, secondary */}
      <div className="border-t border-gray-200 pt-4" data-id="element-134" data-mp="3vt1ffh8y4mxd">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2.5 block" data-id="element-135" data-mp="dmdapsht61s8">
          Moduli Attivi
        </span>
        <div className="grid grid-cols-4 gap-2" data-id="element-136" data-mp="8m8wk94dhj56">
          <GridCell icon={Shield} label="Policy Check" value="Active" statusColor="text-emerald-500" data-id="element-137" data-mp="8pmth1u9p058" />
          <GridCell icon={EyeOff} label="PII Masking" value="Active" statusColor="text-emerald-500" data-id="element-138" data-mp="1k2fn6i6mbynx" />
          <GridCell icon={FileText} label="Logging & Audit" value="Active" statusColor="text-emerald-500" data-id="element-139" data-mp="1s353uhrqa8f2" />
          <GridCell icon={CreditCard} label="Cost Control" value="Warning" statusColor="text-amber-500" data-id="element-140" data-mp="1w4gn85k0dxet" />
        </div>
      </div>
    </div>;
};
export default memo(EngineNode);